import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { Button, ButtonGroup } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { ChartTitleAndDescription, FormInput } from 'src/components/molecules';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import * as Types from 'src/types';
import { CHART_IDS, trackChartViewChanged } from 'src/utils/analytics';
import { DateFormats, formatDate } from 'src/utils/time';
import {
  ChartComponentContainer,
  DownloadButtonContainer,
  GraphButtonContainer,
  GraphPanelContainer,
  IntroComponentContainer,
  StyledTabs,
} from './GraphPanel.styles';
import { getPrintStyles } from './getPrintStyles';

const PRINT_TIMEOUT_MS = 15000;

interface GraphPanelProps {
  onDownloadInsights?: (value: DefaultOption, printChart: () => void) => void;
  onFiltersOpen: () => void;
  chartTitle: string;
  chartTitleDescription?: string;
  headers: string[];
  panels: React.ReactNode[];
  dashboard: 'organisational' | 'operational';
  chartId: CHART_IDS;
  buttonGroupOptions?: string[];
  setSelectedButtonGroupOption?: (option: string) => void;
  selectedButtonGroupOption?: string;
  pdfDownloadRef: React.RefObject<HTMLDivElement | null>;
  pdfDownloadWidth?: number;
  pdfDownloadHeight?: number;
  togglePdfDownloading?: Dispatch<SetStateAction<boolean>>;
}

export const GraphPanel = ({
  onDownloadInsights,
  onFiltersOpen,
  chartTitle,
  chartTitleDescription,
  headers,
  panels,
  dashboard,
  chartId,
  selectedButtonGroupOption,
  buttonGroupOptions,
  setSelectedButtonGroupOption,
  pdfDownloadRef,
  pdfDownloadWidth,
  pdfDownloadHeight,
  togglePdfDownloading,
}: GraphPanelProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const printWatchdog = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Restores the on-screen layout after the print content has been captured.
  // Must run on both success and failure: if it is skipped the chart is left
  // stuck at its fixed print dimensions until the page is reloaded.
  const resetPdfLayout = useCallback(() => {
    if (printWatchdog.current) {
      clearTimeout(printWatchdog.current);
      printWatchdog.current = undefined;
    }
    if (pdfDownloadRef.current) {
      pdfDownloadRef.current.style.width = '100%';
      pdfDownloadRef.current.style.height = '100%';
    }
    togglePdfDownloading?.(false);
  }, [pdfDownloadRef, togglePdfDownloading]);

  useEffect(() => () => clearTimeout(printWatchdog.current), []);

  const print = useReactToPrint({
    contentRef: pdfDownloadRef as React.RefObject<HTMLDivElement>,
    documentTitle: `${chartTitle}_${formatDate(
      new Date(),
      DateFormats.DD_MMM_YYYY_DASH,
    )}.pdf`,
    onBeforePrint: async () => {
      if (pdfDownloadRef.current) {
        pdfDownloadRef.current.style.width = `${pdfDownloadWidth ?? 1000}px`;
        pdfDownloadRef.current.style.height = `${pdfDownloadHeight ?? 500}px`;
      }
      togglePdfDownloading?.(true);

      // `ignoreGlobalStyles` removes the stylesheet wait that used to hang here,
      // but the library still waits on any <img> in the chart and still bails out
      // silently when it finds nothing to print — in both cases without calling
      // `onAfterPrint` or `onPrintError`, leaving the panel pinned at its print
      // dimensions until the page is reloaded. Safe to fire while the dialog is
      // open: it renders a clone held in the iframe, not the live DOM.
      printWatchdog.current = setTimeout(resetPdfLayout, PRINT_TIMEOUT_MS);

      // `togglePdfDownloading` re-renders the panel into its print layout
      // (expanded legends, applied-filters table, resized chart). Wait for that
      // render to paint before the content is cloned, otherwise the PDF
      // captures the pre-resize DOM.
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
    },
    onAfterPrint: resetPdfLayout,
    onPrintError: (errorLocation, error) => {
      resetPdfLayout();
      console.error(
        `Failed to print chart "${chartTitle}" at ${errorLocation}`,
        error,
      );
    },
    ignoreGlobalStyles: true,
    pageStyle: getPrintStyles(
      pdfDownloadWidth ?? 1000,
      pdfDownloadHeight ? pdfDownloadHeight + 100 : 600,
    ),
  });
  const organisation = useSelector((state: Types.State) => state.organisation);
  const features = useSelector((state: Types.State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const handleTrackChartViewChange = (index: number) => {
    if (organisation.organisation && segmentEnabled) {
      trackChartViewChanged(
        organisation.organisation.id,
        dashboard,
        chartId,
        headers[index],
      );
    }
  };
  return (
    <GraphPanelContainer>
      <IntroComponentContainer>
        <ChartTitleAndDescription
          chartTitle={chartTitle}
          chartTitleDescription={chartTitleDescription}
        />
        <DownloadButtonContainer>
          <FormInput
            variant={Types.FormInputVariant.DROPDOWN}
            data-testid={`download-insights-${chartTitle}`}
            buttonText="Download insights"
            dropdownButtonSize="small"
            hideDropdownIndicator={true}
            options={[
              { label: 'CSV', value: 'csv' },
              { label: 'XLS', value: 'xls' },
              { label: 'PDF', value: 'pdf' },
            ]}
            onChange={(value) => {
              if (onDownloadInsights) {
                onDownloadInsights(value as DefaultOption, print);
              } else {
                print();
              }
            }}
            controlShouldRenderValue={false}
          />
        </DownloadButtonContainer>
      </IntroComponentContainer>
      <ChartComponentContainer>
        <StyledTabs
          activeTab={activeTab}
          onClick={setActiveTab}
          headers={headers}
          panels={panels}
          handleTracking={handleTrackChartViewChange}
        />

        <GraphButtonContainer>
          {setSelectedButtonGroupOption &&
            selectedButtonGroupOption &&
            buttonGroupOptions && (
              <ButtonGroup
                options={buttonGroupOptions}
                setSelectedOption={setSelectedButtonGroupOption}
                size="small"
                selectedOption={selectedButtonGroupOption}
              />
            )}
          <Button size={'small'} variant={'secondary'} onClick={onFiltersOpen}>
            <StyledIconInButton
              size="small"
              position="left"
              className="uil uil-sliders-v-alt"
            />
            {'Chart filters'}
          </Button>
        </GraphButtonContainer>
      </ChartComponentContainer>
    </GraphPanelContainer>
  );
};
